<!-- FloatingButton.vue -->
<!-- 
 * 悬浮按钮组件
 * 位于页面右下角的固定位置按钮
 * 功能与MoreButton一致，支持路由导航
 * 设计为常见的悬浮按钮样式，考虑国际化文本长度适配
 -->
<template>
  <div 
    ref="draggableBtn"
    class="floating-button" 
    :class="{ 'dark-mode': darkMode, 'expanded': isExpanded }" 
    :style="style"
    @click="handleClick"
  >
    <!-- @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave" -->
    <transition name="fade">
      <div v-if="isExpanded" class="floating-button-text">{{ text }}</div>
    </transition>
    <div class="floating-button-icon">
      <SvgIcon icon="heroicons:arrow-long-right" />
    </div>
    
    <!-- 拖拽提示图标 -->
    <div class="drag-hint">
      <SvgIcon icon="heroicons:arrows-pointing-out" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import { useDraggable, useWindowSize } from '@vueuse/core';
import SvgIcon from '@/components/SvgIcon.vue';

interface Props {
  /** 按钮显示文本 */
  text?: string;
  /** 是否使用深色模式 */
  darkMode?: boolean;
  /** 目标路由路径 */
  to?: string;
}

const props = withDefaults(defineProps<Props>(), {
  text: '了解更多',
  darkMode: false,
  to: ''
});

const router = useRouter();
const isExpanded = ref(true);
const draggableBtn = ref<HTMLElement | null>(null);

// 获取窗口尺寸
const { width: windowWidth, height: windowHeight } = useWindowSize();

// 固定的边距值
const FIXED_RIGHT_MARGIN = 20;
const FIXED_BOTTOM_MARGIN = 20;

// 初始位置（临时值，会在 onMounted 中更新）
const initialX = windowWidth.value - 260;
const initialY = windowHeight.value - 88;

// 使用 VueUse 的 useDraggable
const { x, y, style, isDragging } = useDraggable(draggableBtn, {
  initialValue: { x: initialX, y: initialY },
  preventDefault: true,
  stopPropagation: true,
});

/**
 * 组件挂载后，根据实际元素宽高重新计算位置
 */
onMounted(async () => {
  await nextTick();
  if (draggableBtn.value) {
    const rect = draggableBtn.value.getBoundingClientRect();
    const newX = windowWidth.value - rect.width - FIXED_RIGHT_MARGIN;
    const newY = windowHeight.value - rect.height - FIXED_BOTTOM_MARGIN;
    x.value = newX;
    y.value = newY;
  }
});

/**
 * 处理鼠标进入事件，展开按钮显示文本
 */
const handleMouseEnter = () => {
  isExpanded.value = true;
};

/**
 * 处理鼠标离开事件，收起按钮隐藏文本
 */
const handleMouseLeave = () => {
  isExpanded.value = false;
};

/**
 * 处理点击事件，如有目标路由则导航
 * 防止拖拽时触发点击
 */
const handleClick = () => {
  if (!isDragging.value && props.to) {
    router.push(props.to);
  }
};
</script>

<style scoped lang="scss">
@use '@/styles/scss/typography.scss';
@use '@/styles/scss/mixins.scss';

.floating-button {
  position: fixed !important;
  width: 48px;
  height: 48px;
  background-color: #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  overflow: hidden;
  transition: all 0.6s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: move;
  z-index: 1000;
  border-radius: 50%;
  
  /* 应用玻璃效果 */
  @include mixins.glass-effect(0.7, 5px);
  
  // 优化移动端触摸
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;

  .floating-button-icon {
    // 添加跳动动画
    animation: bounce-right 0.5s ease infinite;
  }

  // 拖拽时的样式
  &:active {
    cursor: grabbing;
    transform: scale(1.05);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.3);
  }
  
  &:hover {
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.25);
    transform: translateY(-2px);

    .floating-button-icon,
    .iconify {
      animation: bounce-right 0.5s ease infinite;
    }
    
    .drag-hint {
      opacity: 1;
    }
  }

  &.expanded {
    width: auto;
    max-width: 220px;
    border-radius: 24px;
    padding-left: 16px;
    padding-right: 16px;
    transition: all 0.6s cubic-bezier(0.25, 0.8, 0.25, 1);
  }

  &.dark-mode {
    background-color: rgba(30, 30, 30, 0.9);
    border: 1px solid rgba(255, 255, 255, 0.15);

    .floating-button-text {
      color: #ffffff;
    }

    .floating-button-icon {
      color: #ffffff;
    }

    &:hover {
      background-color: rgba(40, 40, 40, 0.9);
    }
  }

  &-text {
    @extend %button-text;
    white-space: nowrap;
    margin-right: 12px;
    font-size: 14px;
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &-icon {
    width: 48px;
    height: 48px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 22px;
    color: #333333;
    flex-shrink: 0;
  }
  
  // 拖拽提示图标
  .drag-hint {
    position: absolute;
    top: 4px;
    left: 4px;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(51, 51, 51, 0.5);
    font-size: 14px;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes bounce-right {

  0%,
  100% {
    transform: translateX(0);
  }

  50% {
    transform: translateX(4px);
  }
}

/* 移动端适配 */
@media (max-width: 768px) {
  .floating-button {
    width: 44px;
    height: 44px;

    .floating-button-icon {
      font-size: 18px;
      width: 44px;
      height: 44px;
    }

    &.expanded {
      max-width: 200px;

      .floating-button-text {
        font-size: 12px;
      }
    }
    
    .drag-hint {
      width: 18px;
      height: 18px;
      font-size: 12px;
    }
  }
}
</style>